// Inbox.jsx - Two ways to read the same conversations.
//
// "Live conversations" is Surge's own embedded inbox, an iframe from
// embed.surge.app authorised by a short lived JWT we mint per person per page
// load. It warns us before the token expires and we hand it a fresh one, so a
// long shift never drops out. Building our own realtime inbox would be weeks of
// work to end up with something worse.
//
// "Message log" is our own mirror, and it is the one that keeps working when
// Surge is not configured, when the iframe is blocked, or when somebody wants
// to see outbound and inbound in one list. It updates itself: lib/live.js
// pushes an inbound text the moment the webhook lands, and falls back to a
// timer if the stream cannot stay open.
//
// LEGEND
//   1. SurgeInbox   the iframe and its token rotation
//   2. Recent       the local mirror, updating itself
//   3. Inbox        the page

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import clsx from "clsx";

import { useAsync } from "../lib/hooks.js";
import { useLiveOrPoll } from "../lib/live.js";
import { adminApi } from "../lib/api.js";
import { ago, phone, truncate } from "../lib/format.js";
import { Panel, Loading, Alert, Empty, Tag, Button } from "../ui/kit.jsx";
import { Message, Inbox as InboxIcon, ArrowUpRight, Refresh } from "../ui/icons.jsx";

const EMBED_ORIGIN = "https://embed.surge.app";

////////// 1. EMBED //////////

function SurgeInbox() {
  const frame = useRef(null);
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);

  const fetchToken = useCallback(async () => {
    const result = await adminApi.inboxToken();
    return result.token;
  }, []);

  useEffect(() => {
    let alive = true;
    fetchToken()
      .then((value) => { if (alive) setToken(value); })
      .catch((problem) => { if (alive) setError(problem); });
    return () => { alive = false; };
  }, [fetchToken]);

  // Surge posts a token_expiring message before the JWT dies. Answer it with a
  // fresh one and the iframe never reloads.
  useEffect(() => {
    const onMessage = async ({ data, origin }) => {
      if (origin !== EMBED_ORIGIN || data?.type !== "token_expiring") return;
      try {
        const next = await fetchToken();
        frame.current?.contentWindow?.postMessage({ type: "update_token", data: { token: next } }, EMBED_ORIGIN);
      } catch {
        // The next page load will get a new one.
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [fetchToken]);

  if (error) {
    return (
      <Alert tone="warning" title="The live inbox is not connected">
        {error.message} Texts still send and arrive, and every one of them is on the order it belongs to.
        Add SURGE_API_KEY and SURGE_ACCOUNT_ID to the server environment to switch this on.
      </Alert>
    );
  }

  if (!token) return <Loading label="Connecting to Surge" />;

  // dvh rather than vh, so the frame does not run under a phone's browser
  // toolbar. What comes off the top of it is the header, the page padding and
  // the dashboard's own bottom bar.
  return (
    <iframe
      ref={frame}
      title="Text message inbox"
      src={`${EMBED_ORIGIN}/conversations?token=${encodeURIComponent(token)}`}
      className="w-full border border-rule bg-card"
      style={{ height: "min(calc(100dvh - 15rem), 760px)", minHeight: "26rem" }}
    />
  );
}

////////// 2. LOCAL MIRROR //////////

// The one line that says whether "live" is true right now. A feature that has
// quietly stopped working should say so rather than look identical to one that
// is working, which is the whole reason this is on screen.
function ConnectionDot({ state }) {
  const label = {
    open: "Live",
    connecting: "Connecting",
    retrying: "Reconnecting, checking on a timer",
    unsupported: "This browser has no live stream, checking on a timer",
    idle: "Idle",
  }[state] || state;

  return (
    <span className="inline-flex items-center gap-1.5 text-[11.5px] text-muted" title={label}>
      <span className={clsx(
        "w-1.5 h-1.5 rounded-full shrink-0",
        state === "open" ? "bg-positive" : state === "retrying" ? "bg-caution" : "bg-faint"
      )} />
      {label}
    </span>
  );
}

function Recent() {
  const { data, loading, error, reload, setData } = useAsync((signal) => adminApi.messages({ limit: 40 }, signal), []);
  const [arrived, setArrived] = useState(0);

  // One handler for both paths. The stream hands us the message it just stored,
  // which is enough to show it instantly; the reload behind it is what keeps the
  // list honest about anything that happened while this tab was hidden.
  const onLive = useCallback((payload) => {
    if (payload) setArrived((count) => count + 1);
    reload();
  }, [reload]);

  const connection = useLiveOrPoll(["message.inbound", "message.status"], onLive);

  // Anything unread that is on screen has now been seen by a person.
  const unreadIds = (data?.items || []).filter((m) => m.direction === "inbound" && !m.readAt).map((m) => m._id);
  const markSeen = async () => {
    if (!unreadIds.length) return;
    setData((previous) => ({
      ...previous,
      unread: 0,
      items: previous.items.map((m) => (unreadIds.includes(m._id) ? { ...m, readAt: new Date().toISOString() } : m)),
    }));
    try { await adminApi.markRead(unreadIds); } catch { reload(); }
  };

  if (loading && !data) return <Loading />;
  if (error && !data) return <Alert tone="error">{error.message}</Alert>;

  return (
    <Panel>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3 border-b border-rule">
        <div className="flex items-center gap-3 min-w-0">
          <p className="eyebrow">Everything sent and received</p>
          <ConnectionDot state={connection} />
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {data?.unread > 0 && (
            <Button size="sm" variant="quiet" onClick={markSeen}>
              Mark {data.unread} read
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={reload}><Refresh size={14} /> Refresh</Button>
        </div>
      </div>

      {arrived > 0 && (
        <p className="px-4 py-2 text-[12.5px] text-bronze-dark bg-bronze-wash border-b border-rule-soft">
          {arrived === 1 ? "A text arrived" : `${arrived} texts arrived`} while you were on this page.
        </p>
      )}

      {!data?.items?.length ? (
        <Empty title="No messages yet" icon={Message}>
          Outbound texts appear here as soon as they send. Inbound ones need the Surge webhook
          pointed at this API, which the Settings page prints the exact address for.
        </Empty>
      ) : (
        <ul className="divide-y divide-rule-soft max-h-[min(60dvh,560px)] overflow-y-auto overscroll-contain">
          {data.items.map((message) => {
            const unread = message.direction === "inbound" && !message.readAt;
            return (
              <li
                key={message._id}
                className={clsx("px-4 py-3 transition-colors", unread ? "bg-bronze-wash/50" : "hover:bg-surface")}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-[13px] font-semibold text-ink num">
                    {unread && <span className="inline-block w-1.5 h-1.5 rounded-full bg-bronze mr-1.5 align-middle" aria-label="Unread" />}
                    {message.direction === "inbound" ? phone(message.from) : phone(message.to)}
                    {message.orderNumber ? (
                      <Link to={`/admin/orders/${message.orderNumber}`} className="ml-2 font-normal text-muted hover:text-ink">
                        {message.orderNumber}
                      </Link>
                    ) : (
                      message.direction === "inbound" && <span className="ml-2 font-normal text-faint">No matching order</span>
                    )}
                  </p>
                  <span className="flex items-center gap-2 shrink-0">
                    <Tag tone={message.direction === "inbound" ? "bronze" : "neutral"}>{message.direction}</Tag>
                    <span className="text-[11.5px] text-faint">{ago(message.createdAt)}</span>
                  </span>
                </div>
                <p className="text-[13.5px] text-muted mt-1 leading-snug">{truncate(message.body || message.subject || "", 180)}</p>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

////////// 3. PAGE //////////

export default function Inbox() {
  const [view, setView] = useState("live");

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="display display-lg text-ink">Inbox</h1>
          <p className="hint mt-1">Texts land here and on the order they belong to.</p>
        </div>
        <div className="flex gap-1.5 w-full sm:w-auto">
          {[["live", "Live conversations"], ["log", "Message log"]].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setView(value)}
              className={`btn btn-sm flex-1 sm:flex-none ${view === value ? "btn-ink" : "btn-quiet"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      {view === "live" ? <SurgeInbox /> : <Recent />}

      <p className="hint mt-4 flex items-center gap-2">
        <InboxIcon size={15} />
        A customer replying to any of our texts threads onto their order automatically.
        <a href="https://app.surge.app" target="_blank" rel="noopener noreferrer" className="link text-ink inline-flex items-center gap-1">
          Surge dashboard <ArrowUpRight size={13} />
        </a>
      </p>
    </div>
  );
}
